import React, { createContext, useState, useContext, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (item, quantity, selectedOptions = [], selectedSettings = {}) => {
    const optionsPrice = selectedOptions.reduce((sum, opt) => sum + (Number(opt.additionalPrice) || 0), 0);
    const itemTotal = (Number(item.price) + optionsPrice) * quantity;

    const cartItem = {
      menuItem: item.id || item._id,
      name: item.name,
      price: Number(item.price),
      quantity,
      selectedOptions,
      selectedSettings,
      itemTotal,
      image: item.image,
      availability: item.availability
    };

    setCart(prevCart => {
      // Check if item with same options and settings already exists
      const existingIndex = prevCart.findIndex(
        ci => ci.menuItem === cartItem.menuItem &&
          JSON.stringify(ci.selectedOptions) === JSON.stringify(cartItem.selectedOptions) &&
          JSON.stringify(ci.selectedSettings) === JSON.stringify(cartItem.selectedSettings)
      );

      if (existingIndex !== -1) {
        const newCart = [...prevCart];
        newCart[existingIndex].quantity += quantity;
        newCart[existingIndex].itemTotal += itemTotal;
        return newCart;
      }

      return [...prevCart, cartItem];
    });
  };

  const removeFromCart = (index) => {
    setCart(prevCart => prevCart.filter((_, i) => i !== index));
  };

  const updateQuantity = (index, quantity) => {
    if (quantity < 1) return;

    setCart(prevCart => {
      const newCart = [...prevCart];
      const item = newCart[index];
      const optionsPrice = item.selectedOptions.reduce((sum, opt) => sum + (Number(opt.additionalPrice) || 0), 0);
      item.quantity = quantity;
      item.itemTotal = (Number(item.price) + optionsPrice) * quantity;
      return newCart;
    });
  };

  const clearCart = () => {
    setCart([]);
  };

  const getTotalAmount = () => {
    return cart.reduce((sum, item) => sum + item.itemTotal, 0);
  };

  const getItemCount = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getTotalAmount,
        getItemCount
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

